import { useEffect, useState, useCallback } from "react";
import api, { apiError, API } from "@/lib/api";
import { money, fmtDate } from "@/lib/ui";
import { RODEO_EVENTS } from "@/components/Onboarding";
import { getChoices } from "@/lib/choices";
import { useFilters } from "@/context/FiltersContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Award, Plus, Loader2, CheckCircle2, Clock, Pencil, Trash2, BadgeCheck, Upload, Film, X } from "lucide-react";

const EMPTY = { rodeo_id: "", rodeo_name: "", event: "", date: "", outcome: "time", placed: true, time: "", score: "", place: "", amount_won: "", notes: "", official: false, media_path: "", media_type: "", media_name: "" };

const OUTCOMES = {
  rough: [{ k: "scored", l: "Scored", good: true }, { k: "buck_off", l: "Buck off" }],
  team: [{ k: "time", l: "Time", good: true }, { k: "no_time", l: "No time" }, { k: "header_missed", l: "Header missed" }, { k: "heeler_missed", l: "Heeler missed" }],
  timed: [{ k: "time", l: "Time", good: true }, { k: "no_time", l: "No time" }],
  barrel: [{ k: "time", l: "Time", good: true }, { k: "no_time", l: "No time" }, { k: "knocked_barrel", l: "Knocked barrel (+5)" }, { k: "off_pattern", l: "Off pattern" }],
};
function outcomeSet(event) {
  const e = (event || "").toLowerCase();
  if (/(bronc|bareback|bull|steer riding)/.test(e)) return OUTCOMES.rough;
  if (e.includes("team roping")) return OUTCOMES.team;
  if (e.includes("barrel")) return OUTCOMES.barrel;
  return OUTCOMES.timed;
}
const outcomeMeta = (event, k) => outcomeSet(event).find((o) => o.k === k);
const isGood = (event, k) => !!outcomeMeta(event, k)?.good;
const outcomeLabel = (event, k) => outcomeMeta(event, k)?.l || (k || "");

export default function MyResults({ m }) {
  const [results, setResults] = useState(null);
  const [rodeos, setRodeos] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [saving, setSaving] = useState(false);
  const choices = getChoices();
  const { events: userEvents } = useFilters();

  const load = useCallback(() => api.get("/me/results").then((r) => setResults(r.data)).catch(() => setResults([])), []);
  useEffect(() => {
    load();
    api.get("/rodeos").then((r) => setRodeos(r.data)).catch(() => {});
  }, [load]);

  const openAdd = () => setDlg({ mode: "add", form: { ...EMPTY } });
  const openEdit = (r) => setDlg({ mode: "edit", form: { ...EMPTY, ...r, amount_won: r.amount_won ?? "" } });

  const save = async () => {
    const f = dlg.form;
    if (!f.rodeo_name.trim()) return toast.error("Pick or type a rodeo");
    if (!f.event) return toast.error("Pick an event");
    setSaving(true);
    const good = isGood(f.event, f.outcome);
    const payload = { ...f, placed: good, amount_won: good ? (parseFloat(f.amount_won) || 0) : 0 };
    try {
      if (dlg.mode === "add") await api.post("/me/results", payload);
      else await api.put(`/me/results/${f.id}`, payload);
      setDlg(null); await load();
      toast.success(dlg.mode === "add" ? "Result logged" : "Result updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const confirmOfficial = async (r) => {
    try { await api.put(`/me/results/${r.id}`, { ...r, amount_won: r.amount_won || 0, official: true }); await load(); toast.success("Marked official"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (r) => {
    try { await api.delete(`/me/results/${r.id}`); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const title = m ? "text-slate-100" : "text-[#14181F]";

  return (
    <section data-testid="my-results-section">
      <div className="flex items-center justify-between mb-2">
        <div className={`text-[11px] font-bold tracking-wider uppercase ${m ? "text-slate-500" : "text-slate-400"}`}>My Results</div>
        <button onClick={openAdd} data-testid="add-result-btn" className="flex items-center gap-1.5 text-xs font-semibold text-[#C8000F] hover:underline">
          <Plus className="w-3.5 h-3.5" /> Log a result
        </button>
      </div>

      {results === null ? (
        <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>
      ) : results.length === 0 ? (
        <div className={`rounded-xl border border-dashed p-8 text-center text-sm ${m ? "border-white/15 text-slate-400" : "border-[#D7DBE0] text-slate-500"}`}>
          <Award className="w-5 h-5 mx-auto mb-2 opacity-60" />
          No results yet. Log your run — time, score, cheque, or a miss — and add a photo or video.
        </div>
      ) : (
        <div className="space-y-3">
          {results.map((r) => {
            const good = isGood(r.event, r.outcome);
            return (
              <div key={r.id} className={`rounded-xl border p-4 ${card}`} data-testid={`result-card-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className={`font-display font-bold ${title}`}>{r.rodeo_name} <span className="text-slate-400 font-normal">· {r.event}</span></div>
                    <div className="text-xs text-slate-400">{r.date ? fmtDate(r.date) : "No date"}{r.place ? ` · ${r.place}` : ""}</div>
                  </div>
                  {r.official ? (
                    <span className="shrink-0 flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded bg-[#E7F6E1] text-[#3B6B1E]" data-testid={`result-official-${r.id}`}><BadgeCheck className="w-3 h-3" /> OFFICIAL</span>
                  ) : (
                    <span className="shrink-0 flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded bg-[#FFF3D6] text-[#8A6100]"><Clock className="w-3 h-3" /> UNOFFICIAL</span>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mt-3">
                  {good ? (
                    <>
                      {r.time && <Stat m={m} label="Time" value={`${r.time}s`} />}
                      {r.score && <Stat m={m} label="Score" value={r.score} />}
                      <Stat m={m} label="Won" value={money(r.amount_won || 0)} accent />
                    </>
                  ) : (
                    <span className="rounded-lg px-3 py-1.5 bg-[#FDE7E9] text-[#9E0008] text-sm font-bold" data-testid={`result-miss-${r.id}`}>{outcomeLabel(r.event, r.outcome)}</span>
                  )}
                </div>

                {r.notes && <div className="text-xs text-slate-400 mt-2">{r.notes}</div>}
                {r.media_path && <MediaView path={r.media_path} type={r.media_type} />}

                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-dashed border-white/10">
                  {!r.official && (
                    <button onClick={() => confirmOfficial(r)} data-testid={`confirm-official-${r.id}`} className="flex items-center gap-1.5 text-xs font-semibold text-[#3B6B1E] hover:underline">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Confirm official
                    </button>
                  )}
                  <button onClick={() => openEdit(r)} data-testid={`edit-result-${r.id}`} className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-[#C8000F]"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                  <button onClick={() => remove(r)} data-testid={`delete-result-${r.id}`} className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-[#C8000F]"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ResultDialog m={m} dlg={dlg} setDlg={setDlg} rodeos={rodeos} choices={choices} userEvents={userEvents} onSave={save} saving={saving} />
    </section>
  );
}

function Stat({ m, label, value, accent }) {
  return (
    <div className={`rounded-lg px-3 py-1.5 ${m ? "bg-black/20" : "bg-[#FAFBFC]"}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`font-display font-bold text-sm ${accent ? "text-[#C8000F]" : (m ? "text-slate-100" : "text-[#14181F]")}`}>{value}</div>
    </div>
  );
}

function MediaView({ path, type }) {
  const token = localStorage.getItem("rp_token");
  const src = `${API}/files/${path}?auth=${token}`;
  const isVideo = (type || "").startsWith("video");
  return (
    <div className="mt-3 rounded-lg overflow-hidden border border-white/10 max-w-xs" data-testid="result-media">
      {isVideo
        ? <video src={src} controls className="w-full max-h-64 bg-black" />
        : <img src={src} alt="run" className="w-full max-h-64 object-cover" />}
    </div>
  );
}

function ResultDialog({ m, dlg, setDlg, rodeos, choices, userEvents, onSave, saving }) {
  const [uploading, setUploading] = useState(false);
  const open = !!dlg;
  const f = dlg?.form || EMPTY;
  const set = (patch) => setDlg((d) => ({ ...d, form: { ...d.form, ...patch } }));
  const DLG = m ? "bg-[#161B22] text-slate-100 border border-white/10 max-w-lg" : "bg-white text-[#14181F] border border-[#E4E7EB] max-w-lg";
  const inpStyle = m ? "bg-white/5 border-white/10 text-slate-100 placeholder-slate-500" : "bg-white border-[#D7DBE0] text-[#14181F] placeholder-slate-400";
  const inpCls = `w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F] ${inpStyle}`;
  const lbl = "text-[11px] font-bold tracking-wide uppercase text-slate-400 mb-1 block";

  const outcomes = outcomeSet(f.event);
  const good = isGood(f.event, f.outcome);
  const selRodeo = rodeos.find((r) => r.id === f.rodeo_id);
  const eventOpts = selRodeo?.events?.length ? selRodeo.events : RODEO_EVENTS;

  const onRodeo = (e) => {
    const v = e.target.value;
    if (v === "__other") return set({ rodeo_id: "", rodeo_name: "" });
    const r = rodeos.find((x) => x.id === v);
    if (!r) return set({ rodeo_id: v });
    const ch = choices[r.id] || {};
    const perf = (r.performances || []).find((p) => p.id === ch.pref1) || (r.performances || [])[0];
    const myEv = (r.events || []).find((ev) => userEvents?.has?.(ev));
    const nextEvent = myEv || (r.events || [])[0] || f.event;
    set({ rodeo_id: v, rodeo_name: r.name, date: perf?.date || "", event: nextEvent, outcome: outcomeSet(nextEvent)[0].k });
  };

  const onEvent = (e) => {
    const ev = e.target.value;
    const nextSet = outcomeSet(ev);
    const keepOutcome = nextSet.some((o) => o.k === f.outcome) ? f.outcome : nextSet[0].k;
    set({ event: ev, outcome: keepOutcome });
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/me/results/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set({ media_path: data.path, media_type: data.content_type, media_name: data.filename });
      toast.success("Media attached");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && setDlg(null)}>
      <DialogContent className={DLG} data-testid="result-dialog">
        {dlg && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">{dlg.mode === "add" ? "Log a result" : "Edit result"}</DialogTitle>
              <DialogDescription>Pick a rodeo and we'll fill in your event and date. Save it unofficial, confirm official once it's posted.</DialogDescription>
            </DialogHeader>

            <div className="space-y-3 max-h-[62vh] overflow-y-auto pr-1">
              <div>
                <label className={lbl}>Rodeo</label>
                <select value={f.rodeo_id || "__other"} onChange={onRodeo} data-testid="result-rodeo-select" className={inpCls}>
                  <option value="__other">— Type it in below —</option>
                  {rodeos.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                {!f.rodeo_id && <input value={f.rodeo_name} onChange={(e) => set({ rodeo_name: e.target.value })} placeholder="Rodeo name" data-testid="result-rodeo-name" className={`${inpCls} mt-2`} />}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Event</label>
                  <select value={f.event} onChange={onEvent} data-testid="result-event-select" className={inpCls}>
                    <option value="">Select…</option>
                    {eventOpts.map((ev) => <option key={ev} value={ev}>{ev}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Date</label>
                  <input type="date" value={f.date || ""} onChange={(e) => set({ date: e.target.value })} data-testid="result-date" className={inpCls} />
                </div>
              </div>

              <div>
                <label className={lbl}>Outcome</label>
                <div className="flex flex-wrap gap-1.5" data-testid="result-outcome">
                  {outcomes.map((o) => (
                    <button key={o.k} type="button" onClick={() => set({ outcome: o.k })} data-testid={`outcome-${o.k}`}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-md border ${f.outcome === o.k ? (o.good ? "bg-[#3B6B1E] text-white border-[#3B6B1E]" : "bg-[#C8000F] text-white border-[#C8000F]") : (m ? "bg-white/5 text-slate-300 border-white/10" : "bg-white text-slate-600 border-[#D7DBE0]")}`}>{o.l}</button>
                  ))}
                </div>
              </div>

              {good && (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div><label className={lbl}>Time (s)</label><input value={f.time || ""} onChange={(e) => set({ time: e.target.value })} placeholder="8.5" data-testid="result-time" className={inpCls} /></div>
                    <div><label className={lbl}>Score</label><input value={f.score || ""} onChange={(e) => set({ score: e.target.value })} placeholder="78" data-testid="result-score" className={inpCls} /></div>
                    <div><label className={lbl}>Place</label><input value={f.place || ""} onChange={(e) => set({ place: e.target.value })} placeholder="3rd" data-testid="result-place" className={inpCls} /></div>
                  </div>
                  <div><label className={lbl}>Amount won ($)</label><input type="number" value={f.amount_won} onChange={(e) => set({ amount_won: e.target.value })} placeholder="1250" data-testid="result-amount" className={inpCls} /></div>
                </>
              )}

              <div>
                <label className={lbl}>Notes</label>
                <textarea value={f.notes || ""} onChange={(e) => set({ notes: e.target.value })} rows={2} placeholder="How the run went…" data-testid="result-notes" className={inpCls} />
              </div>

              <div>
                <label className={lbl}>Photo or video of the run</label>
                {f.media_path ? (
                  <div className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 ${m ? "border-white/10 bg-white/5" : "border-[#D7DBE0] bg-[#FAFBFC]"}`}>
                    <span className="flex items-center gap-2 text-sm min-w-0"><Film className="w-4 h-4 text-[#C8000F] shrink-0" /><span className="truncate">{f.media_name || "Attached"}</span></span>
                    <button type="button" onClick={() => set({ media_path: "", media_type: "", media_name: "" })} data-testid="result-media-remove" className="text-slate-400 hover:text-[#C8000F]"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <label className={`flex items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-3 text-sm cursor-pointer ${m ? "border-white/15 text-slate-300 hover:bg-white/5" : "border-[#D7DBE0] text-slate-600 hover:bg-[#FAFBFC]"}`} data-testid="result-media-upload">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {uploading ? "Uploading…" : "Upload photo / video"}
                    <input type="file" accept="image/*,video/*" onChange={onFile} className="hidden" disabled={uploading} data-testid="result-media-input" />
                  </label>
                )}
              </div>
            </div>

            <DialogFooter>
              <button onClick={() => setDlg(null)} className="text-sm font-semibold px-4 py-2.5 rounded-lg text-slate-400 hover:text-slate-200">Cancel</button>
              <button onClick={onSave} disabled={saving || uploading} data-testid="save-result-btn" className="flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-semibold px-4 py-2.5 rounded-lg disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />} {dlg.mode === "add" ? "Log result" : "Save"}
              </button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
